import { useEffect, useState } from 'react'

import { api, type ImageVariants } from '../api'

/**
 * Variant URLs already fetched, reused until shortly before they expire. A study
 * session shows the same card several times; without this every showing would spend
 * one of the day's image views (spec §9.3) and a fresh presign.
 */
const cache = new Map<string, { variants: ImageVariants['variants']; until: number }>()
const EXPIRY_MARGIN_MS = 60_000

async function variantsFor(name: string): Promise<ImageVariants['variants']> {
  const hit = cache.get(name)
  if (hit && hit.until > Date.now()) return hit.variants
  const result = await api.imageVariants(name)
  cache.set(name, {
    variants: result.variants,
    until: Date.now() + result.expiresIn * 1000 - EXPIRY_MARGIN_MS,
  })
  return result.variants
}

/**
 * Resolves a logical image name to a presigned variant URL as the card renders.
 *
 * Deck responses carry logical names, not URLs, so a deck response does not expire;
 * the short-lived URL is fetched here instead (spec §4.3).
 */
export function DeckImage({ name, variant = 'card' }: { name: string; variant?: string }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    variantsFor(name)
      .then((variants) => {
        if (!cancelled) setUrl(variants[variant] ?? null)
      })
      .catch(() => {
        if (!cancelled) setUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [name, variant])

  if (!url) return null
  return <img src={url} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} />
}
